import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from './Seo';
import Breadcrumbs, { buildBreadcrumbSchema } from './Breadcrumbs';
import './App.css';
import './JoinUs.css';
import Footer from './Footer';
import Navbar from './Navbar';

const breadcrumbItems = [
  { name: 'Home', path: '/' },
  { name: 'Join Us', path: '/join-us' },
];

const testimonials = [
  {
    quote: "I never expected to feel this good about work. Every call is different — some are light, some are deep — but I walk away from each one knowing I actually helped someone. The income is a bonus on top of that.",
    name: "Nandini",
    location: "Delhi",
    avatar: "/testi-1.webp",
  },
  {
    quote: "Two weeks in, I had already made more than my weekend gig. The platform genuinely vets users — so every conversation feels safe and purposeful. It's not just income, it's something I look forward to.",
    name: "Priya",
    location: "Mumbai",
    avatar: "/testi-2.webp",
  },
  {
    quote: "I log in between classes, pick up a few calls, and by the end of the week my pocket money is sorted. What surprised me most is how real the people on the other side are — no time-wasters, no nonsense.",
    name: "Rahul",
    location: "Bangalore",
    avatar: "/testi-3.webp",
  },
  {
    quote: "Baatcheet changed the way I think about conversations. I used to undervalue my ability to listen — now it pays me. Every session leaves me feeling like I genuinely made a difference.",
    name: "Aryan",
    location: "Hyderabad",
    avatar: "/testi-4.webp",
  },
];

function JoinUs() {
  const [active, setActive] = useState(1);

  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive(prev => (prev + 1) % testimonials.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <Seo
        title="Become a Baatcheet Partner — Earn Money by Talking | Baatcheet"
        description="Join Baatcheet's Partner Program. Set your own hours, get verified, and earn by having real voice conversations."
        path="/join-us"
        jsonLd={buildBreadcrumbSchema(breadcrumbItems)}
      />
      <div className="bg" />
      <div className="confetti">
        <i className="dot s1" /><i className="cbar s2" /><i className="dot s3" /><i className="cbar s4" />
        <i className="dot s5" /><i className="dot s6" /><i className="cbar s7" /><i className="dot s8" />
        <i className="cbar s9" /><i className="dot s10" />
      </div>

      {/* ── Header ── */}
      {/* ── Header ── */}
      <Navbar cta={{ label: 'Join Now →', href: '/partner-landing' }} />
      <Breadcrumbs items={breadcrumbItems} />

      <main>

        {/* ── HERO ── */}
        <section className="ju-hero shell">
          <div className="ju-hero-grid">
            <div className="ju-hero-copy reveal">
              <span className="ju-badge">✦ Partner with India's Fastest-Growing Voice Platform</span>
              <h1 className="ju-title">
                Got a Voice?<br />
                <span className="ju-gold-stamp">Let It Pay.</span>
              </h1>
              <p className="ju-title-sub">Your conversations are worth more than small talk.</p>
              <p className="ju-hero-body">
                Baatcheet connects empathetic, <Link to="/safety">identity-verified</Link> partners with real people who
                <Link to="/talk-to-someone">genuinely need someone to talk to</Link>. Set your own hours, pick your topics, earn
                per minute — and build connections that actually mean something.
              </p>
              <div className="ju-hero-cta">
                <Link className="btn ju-btn-gold" to="/partner-landing">
                  <span className="ju-btn-icon">→</span> Join as a Partner
                </Link>
                <Link className="btn btn-ghost" to="/how-it-works">How it Works</Link>
              </div>
              <div className="ju-trust-row">
                <span className="ju-trust-pill">✅ Verified &amp; Safe</span>
                <span className="ju-trust-pill">💰 Earn from Day 1</span>
                <span className="ju-trust-pill">🕐 Flexible Hours</span>
              </div>
            </div>
            <div className="ju-hero-img reveal">
              <img src="/join-hero.webp" alt="Baatcheet partner community" width="1536" height="1024" fetchpriority="high" />
              <div className="ju-hero-float ju-hf1">
                <span className="ju-float-num">20K+</span>
                <span className="ju-float-lbl">Partners</span>
              </div>
              <div className="ju-hero-float ju-hf2">
                <span className="ju-float-num">₹50K+</span>
                <span className="ju-float-lbl">Top Monthly Earn</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── MARQUEE ── */}
        <div className="stripe">
          <div className="stripe-track">
            <span>20,000+ Partners</span><span>50+ Active Cities</span>
            <span>85% Repeat Rate</span><span>₹2 Cr+ Earned</span>
            <span>Top Earn ₹50,000/mo</span><span>Verified &amp; Safe</span>
            <span>Flexible Hours</span><span>Earn from Day 1</span>
            <span>20,000+ Partners</span><span>50+ Active Cities</span>
            <span>85% Repeat Rate</span><span>₹2 Cr+ Earned</span>
            <span>Top Earn ₹50,000/mo</span><span>Verified &amp; Safe</span>
            <span>Flexible Hours</span><span>Earn from Day 1</span>
          </div>
        </div>

        {/* ── IMPACT STATS ── */}
        <section className="block">
          <div className="shell">
            <div className="reveal">
              <div className="kicker"><span className="kicker-line" /> The Numbers Don't Lie</div>
              <h2 className="h">Built on Trust.<br />Proven by <span className="stamp">Numbers.</span></h2>
              <p className="lead">
                From metro cities to small towns, our partner community keeps growing — not
                because we say it's great, but because real partners keep coming back.
              </p>
            </div>
            <div className="ju-impact-grid reveal">
              <div className="ju-impact-card ju-ic-cyan">
                <div className="ju-impact-num">20,000+</div>
                <div className="ju-impact-lbl">Verified Partners Onboard</div>
                <div className="ju-impact-desc">Real, identity-verified people across India</div>
              </div>
              <div className="ju-impact-card ju-ic-blue">
                <div className="ju-impact-num">50+</div>
                <div className="ju-impact-lbl">Active Cities</div>
                <div className="ju-impact-desc">From metros to tier-2 towns, we're everywhere</div>
              </div>
              <div className="ju-impact-card ju-ic-rose">
                <div className="ju-impact-num">85%</div>
                <div className="ju-impact-lbl">Repeat Call Rate</div>
                <div className="ju-impact-desc">Users keep coming back for quality conversations</div>
              </div>
            </div>
          </div>
        </section>

        {/* ── STEPS ── */}
        <section className="block">
          <div className="shell">
          <div className="ju-steps-intro reveal">
            <div className="ju-steps-intro-left">
              <div className="kicker"><span className="kicker-line" /> Get Started in Minutes</div>
              <h2 className="h">
                Your Three-Step<br />
                Path to <span className="ju-h-stamp">Earning</span>
              </h2>
            </div>
            <div className="ju-steps-intro-right">
              <p className="lead">
                Account, profile, verification — done in under 10 minutes. After that,
                all you need is your voice and the willingness to show up.
              </p>
              <div className="ju-step-chips">
                <div className="ju-step-chip">
                  <span className="ju-sc-num">01</span>
                  <span className="ju-sc-text">Create Your Account</span>
                  <span className="ju-sc-tag">30 sec ⚡</span>
                </div>
                <div className="ju-step-chip">
                  <span className="ju-sc-num">02</span>
                  <span className="ju-sc-text">Build Your Profile</span>
                  <span className="ju-sc-tag">5 min 🎙️</span>
                </div>
                <div className="ju-step-chip">
                  <span className="ju-sc-num">03</span>
                  <span className="ju-sc-text">Verify &amp; Go Live</span>
                  <span className="ju-sc-tag">Instant 🔒</span>
                </div>
              </div>
            </div>
          </div>

          {/* Step 1 */}
          <div className="hiw-step reveal">
            <div className="hiw-step-bg-num">01</div>
            <div className="hiw-step-inner">
              <div className="hiw-step-text">
                <span className="hiw-step-tag">Step 1</span>
                <h2 className="hiw-step-h">Create Your Account</h2>
                <p className="ju-step-accent">Identity-Verified, Always.</p>
                <p className="hiw-step-p">
                  Sign up on the Baatcheet Partner Portal with your mobile number. A
                  one-time OTP gets you in within 30 seconds. We verify every partner —
                  which is exactly why users trust this platform.
                </p>
                <ul className="hiw-list">
                  <li><span className="hiw-li-icon">📱</span> Register with your mobile number</li>
                  <li><span className="hiw-li-icon">🔐</span> Verify instantly with OTP sent to your phone</li>
                  <li><span className="hiw-li-icon">✅</span> 100% verified real people only — no bots</li>
                </ul>
              </div>
              <div className="hiw-step-img-wrap">
                <img className="hiw-step-img" src="/join-signup.webp" alt="Sign up as Baatcheet partner" width="1536" height="1024" loading="lazy" decoding="async" />
                <div className="hiw-sticky hiw-sticky-1">30 sec<br />to sign up! ⚡</div>
              </div>
            </div>
          </div>

          {/* Connector */}
          <div className="hiw-conn">
            <div className="hiw-conn-dash" />
            <div className="hiw-conn-badge" style={{ background: 'var(--gold)' }}>✦</div>
            <div className="hiw-conn-dash" />
          </div>

          {/* Step 2 */}
          <div className="hiw-step reveal">
            <div className="hiw-step-bg-num">02</div>
            <div className="hiw-step-inner hiw-step-flip">
              <div className="hiw-step-img-wrap">
                <img className="hiw-step-img" src="/join-profile.webp" alt="Create your Baatcheet profile" width="1536" height="1024" loading="lazy" decoding="async" />
                <div className="hiw-sticky hiw-sticky-2">Be you,<br />be real ✨</div>
              </div>
              <div className="hiw-step-text">
                <span className="hiw-step-tag">Step 2</span>
                <h2 className="hiw-step-h">Build Your Profile</h2>
                <p className="ju-step-accent">Show the World Who You Are.</p>
                <p className="hiw-step-p">
                  Your profile is your first impression. Add your interests, a bio that
                  reflects your personality, and a voice intro that tells users exactly
                  what kind of conversations they can expect with you.
                </p>
                <ul className="hiw-list">
                  <li><span className="hiw-li-icon">🧩</span> Add hobbies, interests &amp; a short bio</li>
                  <li><span className="hiw-li-icon">🎤</span> Record a voice introduction</li>
                  <li><span className="hiw-li-icon">🖼️</span> Pick an avatar or upload your own</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Connector */}
          <div className="hiw-conn">
            <div className="hiw-conn-dash" />
            <div className="hiw-conn-badge" style={{ background: 'var(--gold)' }}>✦</div>
            <div className="hiw-conn-dash" />
          </div>

          {/* Step 3 */}
          <div className="hiw-step reveal">
            <div className="hiw-step-bg-num">03</div>
            <div className="hiw-step-inner">
              <div className="hiw-step-text">
                <span className="hiw-step-tag">Step 3</span>
                <h2 className="hiw-step-h">Verify &amp; Go Live</h2>
                <p className="ju-step-accent">Lock In Identity. Unlock Income.</p>
                <p className="hiw-step-p">
                  A quick PAN Card upload completes your KYC — this one step is what
                  unlocks your earning potential. It protects the platform, ensures
                  tax compliance, and means your payouts land on time, every time.
                </p>
                <ul className="hiw-list">
                  <li><span className="hiw-li-icon">🪪</span> Upload your PAN Card for identity verification</li>
                  <li><span className="hiw-li-icon">💳</span> Required for TDS compliance &amp; hassle-free payouts</li>
                  <li><span className="hiw-li-icon">🔒</span> Your data is encrypted &amp; 100% secure</li>
                </ul>
              </div>
              <div className="hiw-step-img-wrap">
                <img className="hiw-step-img" src="/join-kyc.webp" alt="KYC verification for Baatcheet" width="1536" height="1024" loading="lazy" decoding="async" />
                <div className="hiw-sticky hiw-sticky-3">100%<br />Secure 🔒</div>
              </div>
            </div>
          </div>
          </div>
        </section>

        {/* ── YOU'RE IN ── */}
        <section className="ju-youre-in">
          <div className="ju-yin-icons" aria-hidden="true">
            <span className="ju-yin-i ju-yin-i1">✦</span>
            <span className="ju-yin-i ju-yin-i2">👋</span>
            <span className="ju-yin-i ju-yin-i3">✦</span>
            <span className="ju-yin-i ju-yin-i4">👋</span>
            <span className="ju-yin-i ju-yin-i5">✦</span>
            <span className="ju-yin-i ju-yin-i6">👋</span>
            <span className="ju-yin-i ju-yin-i7">✦</span>
            <span className="ju-yin-i ju-yin-i8">✦</span>
          </div>
          <div className="shell ju-yin-content reveal">
            <h2 className="ju-yin-title">You're In!</h2>
            <p className="ju-yin-sub">
              Verified. Live. Ready to earn. Your first call request could arrive
              within the hour — answer it, make an impact, and the rest takes care
              of itself.
            </p>
            <Link className="btn ju-btn-gold" to="/partner-landing">→ Start Your Journey</Link>
          </div>
        </section>

        {/* ── NUMBERS ── */}
        <section className="block">
          <div className="shell">
            <div className="ju-numbers-wrap reveal">
              <div className="ju-numbers-copy">
                <div className="kicker"><span className="kicker-line" /> Proof Over Promises</div>
                <h2 className="h">The Proof Is<br />In the <span className="stamp">Payouts.</span></h2>
                <p className="lead" style={{ marginTop: '16px' }}>
                  Every rupee, every minute, every partner — tracked, transparent, and
                  real. No inflated projections, just what our community actually achieved.
                </p>
                <Link className="btn ju-btn-gold" to="/partner-landing" style={{ marginTop: '28px' }}>
                  → Join Now &amp; Start Earning
                </Link>
              </div>
              <div className="ju-earn-grid">
                <div className="ju-earn-card ju-ec-purple">
                  <div className="ju-earn-num">₹50,000+</div>
                  <div className="ju-earn-lbl">Top Partners Income</div>
                  <div className="ju-earn-period">per month</div>
                </div>
                <div className="ju-earn-card ju-ec-cyan">
                  <div className="ju-earn-num">₹2 Cr.+</div>
                  <div className="ju-earn-lbl">Earned by Our Community</div>
                </div>
                <div className="ju-earn-card ju-ec-rose">
                  <div className="ju-earn-num">10M+</div>
                  <div className="ju-earn-lbl">Minutes of Real Conversations</div>
                </div>
                <div className="ju-earn-card ju-ec-gold">
                  <div className="ju-earn-num">1,000+</div>
                  <div className="ju-earn-lbl">Partners Actively Earning Today</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section className="ju-testi-section">
          <div className="shell">
            <div className="reveal" style={{ textAlign: 'center', marginBottom: '52px' }}>
              <div className="kicker" style={{ justifyContent: 'center' }}>
                <span className="kicker-line" /> Real Voices
              </div>
              <h2 className="h" style={{ textAlign: 'center', margin: '14px auto 0' }}>
                From the Voices<br />That Keep <span className="stamp">Showing Up</span>
              </h2>
            </div>

            <div className="ju-testi-card reveal">
              <div className="ju-qm ju-qm-open">"</div>
              <p className="ju-testi-quote">
                "{testimonials[active].quote}"
              </p>
              <p className="ju-testi-name">~{testimonials[active].name}</p>
              <div className="ju-qm ju-qm-close">"</div>
            </div>

            {(() => {
              const n = testimonials.length;
              const prevIdx = (active - 1 + n) % n;
              const nextIdx = (active + 1) % n;
              return (
                <div className="ju-av-carousel reveal">
                  {[prevIdx, active, nextIdx].map((idx) => (
                    <button
                      key={idx}
                      onClick={() => setActive(idx)}
                      className={`ju-av-btn ${idx === active ? 'ju-av-active' : 'ju-av-side'}`}
                      aria-label={`View testimonial from ${testimonials[idx].name}`}
                    >
                      <div className="ju-av-circle">
                        <img src={testimonials[idx].avatar} alt={testimonials[idx].name} loading="lazy" decoding="async" />
                      </div>
                      <span className="ju-av-name">{testimonials[idx].name}</span>
                    </button>
                  ))}
                  <p className="ju-av-tagline">Be part of something amazing!</p>
                </div>
              );
            })()}
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <section className="ju-cta-section shell">
          <div className="ju-cta-card reveal">
            <div className="ju-cta-left">
              <div className="kicker ju-cta-kicker"><span className="kicker-line" /> Your Next Move</div>
              <h2 className="ju-cta-title">Your Mic<br />Is Waiting.</h2>
              <p className="ju-cta-body">
                Thousands of verified partners are already live on the platform.
                The next call request is minutes away — will you be the one to
                pick it up?
              </p>
              <Link className="btn ju-btn-gold ju-btn-partner" to="/partner-landing">
                → Join as a Partner
              </Link>
              <div className="ju-cta-trust">
                <span>✔ Free to join</span>
                <span>✔ Earn from day one</span>
                <span>✔ 100% verified &amp; safe</span>
              </div>
            </div>
            <div className="ju-cta-right">
              <img src="/join-app.webp" alt="Baatcheet partner app interface" width="1086" height="1448" loading="lazy" decoding="async" />
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </>
  );
}

export default JoinUs;
